import React, { useState } from "react";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import ModalBasic from "./ModalBasic.jsx";
import { FaShield, FaCopy, FaTriangleExclamation, FaPrint } from "react-icons/fa6";
import { generateBackupCodes } from "../api/auth.js";
import { addToast, checkSession } from "../store/app/action.js";
import { useDispatch } from "react-redux";

function BackupCodesModal({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await generateBackupCodes();
      
      if (response.status === "success") {
        setCodes(response.codes);
        // Refresh user data to update backup codes count
        try {
          await dispatch(checkSession());
        } catch (error) {
        }
      } else {
        setError(response.message || "Failed to generate backup codes");
      }
    } catch (err) {
      setError("Failed to generate backup codes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyAllCodes = async () => {
    const codesText = codes.join('\n');
    try {
      await navigator.clipboard.writeText(codesText);
      dispatch(
        addToast({
          type: "success",
          message: "Backup codes copied to clipboard!",
          timer: 3,
        })
      );
    } catch (err) {
    }
  };

  const printCodes = () => {
    const printWindow = window.open('', '_blank');
    const codesHtml = `
      <html>
        <head>
          <title>Backup Codes - Saucey Dashboard</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .warning { background: #fee; border: 1px solid #fcc; padding: 15px; margin: 20px 0; border-radius: 5px; }
            .codes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
            .code { border: 1px solid #ccc; padding: 10px; text-align: center; font-family: monospace; font-size: 14px; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Saucey Dashboard - Backup Codes</h1>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="warning">
            <h3>⚠️ Important Security Notice</h3>
            <p><strong>These codes will NOT be displayed again!</strong></p>
            <p>Please store this document in a secure location. Each code can only be used once to access your account.</p>
          </div>
          
          <div class="codes">
            ${codes.map(code => `<div class="code">${code}</div>`).join('')}
          </div>
          
          <div class="footer">
            <p>Keep this document safe and do not share these codes with anyone.</p>
            <p>If you lose these codes, you can generate new ones from your account settings.</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(codesHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const handleClose = () => {
    setCodes([]);
    setError(null);
    onClose();
  };

  const handleSuccess = () => {
    setCodes([]);
    setError(null);
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <ModalBasic
      id="backup-codes-modal"
      modalOpen={isOpen}
      setModalOpen={handleClose}
      width={"2xl"}
    >
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 mx-auto mb-4">
            <FaShield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 text-center mb-2">
            Backup Codes
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-center">
            Backup codes can be used to access your account if you lose your authenticator device.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-center">{error}</p>
          </div>
        )}

        {!codes.length && !loading && (
          <div className="space-y-6">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FaTriangleExclamation className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                    Important Warning
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    Generating new backup codes will permanently delete all existing backup codes. 
                    Make sure you have saved your current codes before proceeding.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton onClick={handleClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={handleGenerate} disabled={loading}>
                {loading ? "Generating..." : "Generate New Codes"}
              </PrimaryButton>
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-500 dark:text-slate-400">Generating backup codes...</p>
            </div>
          </div>
        )}

        {codes.length > 0 && (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h3 className="font-medium text-green-800 dark:text-green-200 mb-2">
                Your Backup Codes
              </h3>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium mb-2">
                  ⚠️ Important: These codes will NOT be displayed again!
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  Please print this page or copy these codes to a secure location. 
                  Each code can only be used once to access your account.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-4">
                {codes.map((code, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-slate-800 p-3 rounded border font-mono text-sm text-center"
                  >
                    {code}
                  </div>
                ))}
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={copyAllCodes}
                  className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                >
                  <FaCopy className="w-4 h-4" />
                  <span>Copy All Codes</span>
                </button>
                <button
                  onClick={printCodes}
                  className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                >
                  <FaPrint className="w-4 h-4" />
                  <span>Print Codes</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <SecondaryButton onClick={handleClose}>
                Close
              </SecondaryButton>
              <PrimaryButton onClick={handleSuccess}>
                I've Saved These Codes
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </ModalBasic>
  );
}

export default BackupCodesModal;
